// src/components/ActiveSessionBanner/ActiveSessionBanner.jsx
import React, { useMemo } from 'react';
import { FaBolt, FaArrowRight, FaXmark } from 'react-icons/fa6';
import './ActiveSessionBanner.less';

/**
 * Banner shown when user has an active charging session but navigates
 * to start a new one (e.g., phone locked → reopened → scanned QR code).
 * Gives user choice to resume active session or continue with new flow.
 */
const ActiveSessionBanner = ({ sessionUid, stationId, status, startedAt, onResume, onDismiss }) => {
  const statusLabel = useMemo(() => {
    switch (status) {
      case 'charging': return 'Charging';
      case 'starting':
      case 'preparing': return 'Starting';
      case 'authorized': return 'Ready to charge';
      case 'pending': return 'Session pending';
      default: return 'Active';
    }
  }, [status]);

  const elapsedText = useMemo(() => {
    if (!startedAt) return null;
    const elapsed = Date.now() - new Date(startedAt).getTime();
    const mins = Math.floor(elapsed / 60000);
    if (mins < 1) return 'just started';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m ago`;
  }, [startedAt]);

  return (
    <div className="active-session-banner">
      <div className="active-session-banner__content">
        <div className="active-session-banner__icon">
          <FaBolt />
        </div>
        <div className="active-session-banner__info">
          <div className="active-session-banner__title">
            You have an active session
          </div>
          <div className="active-session-banner__details">
            <span className={`active-session-banner__status active-session-banner__status--${status}`}>
              {statusLabel}
            </span>
            {stationId && (
              <span className="active-session-banner__station">
                {stationId}
              </span>
            )}
            {elapsedText && (
              <span className="active-session-banner__elapsed">
                {elapsedText}
              </span>
            )}
          </div>
        </div>
        <button
          className="active-session-banner__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <FaXmark />
        </button>
      </div>
      <div className="active-session-banner__actions">
        <button
          className="active-session-banner__resume"
          onClick={onResume}
        >
          Resume Session <FaArrowRight />
        </button>
        <button
          className="active-session-banner__new"
          onClick={onDismiss}
        >
          Start New
        </button>
      </div>
    </div>
  );
};

export default ActiveSessionBanner;
